// src/components/Reviews.js
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';

const reviewsData = [
  {
    id: 1,
    text: "I have used several different server hosting companies through the years and Hybrid Hosting is by far the best one. From reasonable pricing to the personalized support we received when we had issues in our initial setup the team at Hybrid Hosting has been nothing short of amazing. I want to give special thanks to Koby for the extra help in troubleshooting and solving some of the issues we had when we moved our servers over to Hybrid. I know we may have been a bit of a burden but I want to say thanks for taking the extra time to help us and make sure our services were up and running smoothly. Since then we have had 9 months of smooth sailing and minimal downtime to any of our servers. Here is to many more years of great service.",
    author: "Dean Richter (US, September 22, 2024) — Build The Earth, Team USA"
  },
  {
    id: 2,
    text: "I've used many different server hosting companies before and Hybrid Hosting is genuinely one of the best I've used. There were initially a few issues when transferring our server over to Hybrid Hosting however they were extremely quick to respond and willing to help. The customer support and connection is on another level and is admirable. The connection to the server is speedy and Hybrid Hosting has an easy to use, consumer friendly user interface. I am very satisfied with my purchase and hope to be a long lasting customer of Hybrid Hosting.",
    author: "Dactus (US, March 21, 2024)"
  },
  {
    id: 3,
    text: "Great reliable host, and actually care for customers! I moved here from a host which had tons of downtime and problems, and was scummy, which I didn't know at the time of beginning to use them. Moving to this host was an overall great experience! (minus the server importer not working, since the previous host blocked the importer's IP address from connecting lol). Support has been extremely helpful, and quick so far, and they take customer feedback seriously. They also definitely care for their customers, as they are moving data centers to better the experience, and during the move spending the time and money to rent out temp servers to minimize downtime on the client's end. Most hosts would just have the clients deal with the multiple day downtime, but Hybrid is different, and for the better. Their custom panel is also amazing, being a customized version of pterodactyl, it has extra features, looks extremely clean, and works just as smoothly as it looks. Overall great host, would recommend. (For reference, my service is an 8GB minecraft server)",
    author: "lilspojo (US, August 10, 2023)"
  },
];

const Reviews = () => {
  const [currentReview, setCurrentReview] = useState(0);
  const [displayReview, setDisplayReview] = useState(reviewsData[0]);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  const [containerHeight, setContainerHeight] = useState(null);
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  // Auto-cycle every 10 seconds only if the review card is visible.
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setCurrentReview(prev => (prev + 1) % reviewsData.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [isVisible]);

  // Use IntersectionObserver to check if the review card is visible in the middle 2/3 of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: "-17% 0px -17% 0px", // roughly middle 66% of viewport
      }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => {
      if (currentContainer) observer.unobserve(currentContainer);
    };
  }, []);

  // Measure the height of the review content using getBoundingClientRect and add extra padding (40px).
  useLayoutEffect(() => {
    if (contentRef.current) {
      const measuredHeight = contentRef.current.getBoundingClientRect().height;
      setContainerHeight(measuredHeight + 40); // extra 40px padding
    }
  }, [displayReview]);

  // When currentReview changes, trigger a fade/slide transition.
  useEffect(() => {
    if (reviewsData[currentReview].id !== displayReview.id) {
      setTransitionStage("fadeOut");
      const timeout = setTimeout(() => {
        setDisplayReview(reviewsData[currentReview]);
        setTransitionStage("fadeIn");
      }, 500); // 500ms transition
      return () => clearTimeout(timeout);
    }
  }, [currentReview, displayReview]);

  return (
    <section id="reviews" className="my-16 text-center select-none" ref={containerRef}>
      <h2 className="text-2xl font-bold text-white mb-4">Reviews</h2>
      <div
        className="bg-[#212121] border border-[#4d4d4d] rounded-md p-6 max-w-3xl mx-auto relative overflow-hidden"
        style={{ 
          height: containerHeight ? containerHeight : 'auto', 
          transition: 'height 0.5s ease'
        }}
      >
        <div
          ref={contentRef}
          key={displayReview.id}
          className={`transition-all duration-500 ease-in-out transform ${
            transitionStage === "fadeOut" ? "opacity-0 -translate-x-10" : "opacity-100 translate-x-0"
          }`}
        >
          <div className="flex justify-center mb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <svg key={i} className="w-6 h-6 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.955a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.449a1 1 0 00-.364 1.118l1.286 3.955c.3.921-.755 1.688-1.54 1.118l-3.37-2.449a1 1 0 00-1.175 0l-3.37 2.449c-.784.57-1.838-.197-1.539-1.118l1.285-3.955a1 1 0 00-.364-1.118L2.07 9.382c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.955z" />
              </svg>
            ))}
          </div>
          <p className="text-gray-300 italic">"{displayReview.text}"</p>
          <p className="mt-4 text-gray-400">{displayReview.author}</p>
        </div>
      </div>
      <div className="flex justify-center space-x-2 mt-4">
        {reviewsData.map((review, index) => (
          <button
            key={review.id}
            onClick={() => setCurrentReview(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              currentReview === index ? 'bg-blue-900' : 'bg-gray-500'
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
};

export default Reviews;